import { Clock } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

function parseTime(value?: string): { h: string; m: string } {
  const raw = (value ?? '').trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return { h: '', m: '' };
  return {
    h: String(Math.min(23, Number(match[1]))).padStart(2, '0'),
    m: String(Math.min(59, Number(match[2]))).padStart(2, '0'),
  };
}

interface TimePickerProps {
  value?: string;
  onChange: (value: string) => void;
  /** Passe `null` ou `""` para omitir (quando o label fica fora do componente). */
  label?: string | null;
  className?: string;
  id?: string;
}

/** Seletor de horário HH:mm (paridade com bootstrap-timepicker do legado). */
export function TimePicker({
  value,
  onChange,
  label = 'Horário',
  className,
  id,
}: TimePickerProps) {
  const { h, m } = parseTime(value);
  const showLabel = Boolean(label);

  const setPart = (part: 'h' | 'm', next: string) => {
    const hour = part === 'h' ? next : h || '00';
    const minute = part === 'm' ? next : m || '00';
    onChange(`${hour}:${minute}`);
  };

  return (
    <div className={cn(showLabel && 'flex flex-col gap-2', className)}>
      {showLabel ? <Label htmlFor={id}>{label}</Label> : null}
      <div className="field-glass flex h-10 w-full items-center gap-1 rounded-lg border border-input bg-[hsl(var(--glass-bg)/0.5)] px-3 shadow-glass-sm">
        <Clock className="size-4 shrink-0 text-muted-foreground opacity-70" aria-hidden />
        <Select value={h || undefined} onValueChange={(v) => setPart('h', v)}>
          <SelectTrigger
            id={id}
            className="h-8 w-auto min-w-14 shrink-0 border-0 bg-transparent px-1.5 shadow-none focus:ring-0 focus:ring-offset-0"
          >
            <SelectValue placeholder="HH" />
          </SelectTrigger>
          <SelectContent className="max-h-60 min-w-[4.5rem]">
            {HOURS.map((hour) => (
              <SelectItem key={hour} value={hour}>
                {hour}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="shrink-0 text-sm font-medium text-muted-foreground">:</span>
        <Select value={m || undefined} onValueChange={(v) => setPart('m', v)}>
          <SelectTrigger className="h-8 w-auto min-w-14 shrink-0 border-0 bg-transparent px-1.5 shadow-none focus:ring-0 focus:ring-offset-0">
            <SelectValue placeholder="MM" />
          </SelectTrigger>
          <SelectContent className="max-h-60 min-w-[4.5rem]">
            {MINUTES.map((minute) => (
              <SelectItem key={minute} value={minute}>
                {minute}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
